/* eslint-disable */
/**
 * db.service.js
 * IndexedDB storage engine with localStorage fallback and auto-migration.
 */

const DB_NAME = 'triad_offline_db';
const DB_VERSION = 1;
const STORE_NAME = 'keyval';

class DBService {
    constructor() {
        this.db = null;
        this.dbPromise = null;
        this.isSupported = typeof window !== 'undefined' && 'indexedDB' in window;
        if (this.isSupported) {
            this.init();
        }
    }

    async init() {
        if (this.dbPromise) return this.dbPromise;
        if (!this.isSupported) return null;

        this.dbPromise = new Promise((resolve) => {
            try {
                const request = window.indexedDB.open(DB_NAME, DB_VERSION);

                request.onupgradeneeded = (event) => {
                    const db = event.target.result;
                    if (!db.objectStoreNames.contains(STORE_NAME)) {
                        db.createObjectStore(STORE_NAME);
                    }
                };

                request.onsuccess = (event) => {
                    this.db = event.target.result;
                    this.migrateLocalStorage();
                    this.requestPersistentStorage();
                    resolve(this.db);
                };

                request.onerror = (event) => {
                    console.warn('[DBService] IndexedDB open error, falling back to localStorage:', event.target.error);
                    this.db = null;
                    resolve(null);
                };
            } catch (e) {
                console.warn('[DBService] IndexedDB initialization failed:', e);
                this.db = null;
                resolve(null);
            }
        });

        return this.dbPromise;
    }

    /**
     * Requests persistent storage (navigator.storage.persist()) so mobile browsers
     * (especially Safari / iOS) and desktop browsers do not purge IndexedDB / CacheStorage
     * under storage pressure or after 7 days of inactivity.
     */
    async requestPersistentStorage() {
        if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.persist) {
            try {
                const isPersisted = await navigator.storage.persisted();
                if (!isPersisted) {
                    const granted = await navigator.storage.persist();
                    console.log(`[DBService] Persistent storage granted: ${granted}`);
                } else {
                    console.log('[DBService] Storage is already persisted');
                }
            } catch (e) {
                console.warn('[DBService] Error requesting persistent storage:', e);
            }
        }
    }

    /**
     * Migrates existing drawing, notes, and sync keys from localStorage into IndexedDB.
     */
    async migrateLocalStorage() {
        if (typeof localStorage === 'undefined') return;
        try {
            const keysToMigrate = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (
                    key.startsWith('saved-drawing-') ||
                    key.startsWith('notes-playlist-') ||
                    key === 'triad_offline_sync_queue'
                )) {
                    keysToMigrate.push(key);
                }
            }

            for (const key of keysToMigrate) {
                const existing = await this.getItem(key);
                if (existing === null || existing === undefined) {
                    const raw = localStorage.getItem(key);
                    if (raw) {
                        try {
                            const parsed = JSON.parse(raw);
                            await this.setItem(key, parsed);
                        } catch (e) {
                            await this.setItem(key, raw);
                        }
                    }
                }
            }
        } catch (e) {
            console.warn('[DBService] Error during localStorage migration:', e);
        }
    }

    async getItem(key) {
        await this.init();
        if (this.db) {
            return new Promise((resolve) => {
                try {
                    const transaction = this.db.transaction([STORE_NAME], 'readonly');
                    const store = transaction.objectStore(STORE_NAME);
                    const request = store.get(key);

                    request.onsuccess = () => {
                        if (request.result !== undefined) {
                            resolve(request.result);
                        } else {
                            // Fallback to localStorage check
                            resolve(this.getFromLocalStorage(key));
                        }
                    };

                    request.onerror = () => {
                        resolve(this.getFromLocalStorage(key));
                    };
                } catch (e) {
                    resolve(this.getFromLocalStorage(key));
                }
            });
        }
        return this.getFromLocalStorage(key);
    }

    async setItem(key, value) {
        // Also keep localStorage updated for instant synchronous reads when needed
        this.setToLocalStorage(key, value);

        await this.init();
        if (this.db) {
            return new Promise((resolve) => {
                try {
                    const transaction = this.db.transaction([STORE_NAME], 'readwrite');
                    const store = transaction.objectStore(STORE_NAME);
                    const request = store.put(value, key);

                    request.onsuccess = () => resolve(true);
                    request.onerror = (e) => {
                        console.warn('[DBService] Error writing to IndexedDB:', e);
                        resolve(false);
                    };
                } catch (e) {
                    console.warn('[DBService] Exception writing to IndexedDB:', e);
                    resolve(false);
                }
            });
        }
        return true;
    }

    async removeItem(key) {
        if (typeof localStorage !== 'undefined') {
            try {
                localStorage.removeItem(key);
            } catch (e) {}
        }

        await this.init();
        if (this.db) {
            return new Promise((resolve) => {
                try {
                    const transaction = this.db.transaction([STORE_NAME], 'readwrite');
                    const store = transaction.objectStore(STORE_NAME);
                    const request = store.delete(key);

                    request.onsuccess = () => resolve(true);
                    request.onerror = () => resolve(false);
                } catch (e) {
                    resolve(false);
                }
            });
        }
        return true;
    }

    async clear() {
        await this.init();
        if (this.db) {
            return new Promise((resolve) => {
                try {
                    const transaction = this.db.transaction([STORE_NAME], 'readwrite');
                    const store = transaction.objectStore(STORE_NAME);
                    const request = store.clear();
                    request.onsuccess = () => resolve(true);
                    request.onerror = () => resolve(false);
                } catch (e) {
                    resolve(false);
                }
            });
        }
        return true;
    }

    getFromLocalStorage(key) {
        if (typeof localStorage === 'undefined') return null;
        try {
            const raw = localStorage.getItem(key);
            if (raw === null || raw === undefined) return null;
            try {
                return JSON.parse(raw);
            } catch (e) {
                return raw;
            }
        } catch (e) {
            return null;
        }
    }

    setToLocalStorage(key, value) {
        if (typeof localStorage === 'undefined') return;
        try {
            // Avoid storing heavy song payloads or items larger than 25KB in localStorage;
            // IndexedDB is the designated store for large structured datasets.
            if (typeof key === 'string' && (key.includes('::song::') || key.startsWith('song::'))) {
                return;
            }
            const serialized = typeof value === 'string' ? value : JSON.stringify(value);
            if (serialized.length > 25000) {
                return;
            }
            localStorage.setItem(key, serialized);
        } catch (e) {
            // QuotaExceededError is gracefully handled here because IndexedDB stores the real data
            console.warn('[DBService] LocalStorage write skipped (likely quota exceeded, saved in IndexedDB):', e);
        }
    }
}

export const dbService = new DBService();
export default dbService;
